import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking");
    const [attempts, setAttempts] = useState(0);
    const { refresh } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!sessionId) { setStatus("missing"); return; }
        let cancelled = false;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    refresh();
                    return;
                }
                if (attempts >= 30) { setStatus("timeout"); return; }
                setAttempts((a) => a + 1);
                setTimeout(poll, 2000);
            } catch {
                setStatus("error");
            }
        };
        poll();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <main className="container-page py-24 max-w-xl fade-up text-center" data-testid="payment-success">
            {status === "checking" && (
                <>
                    <Loader2 className="w-12 h-12 text-[#2D5D4E] mx-auto mb-6 animate-spin" strokeWidth={1.6} />
                    <h1 className="text-3xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }}>Confirming your payment…</h1>
                    <p className="text-[#515B57]">Hold on a second — Stripe just needs a moment.</p>
                </>
            )}
            {status === "paid" && (
                <>
                    <CheckCircle2 className="w-14 h-14 text-[#3A7D5C] mx-auto mb-6" strokeWidth={1.5} />
                    <h1 className="text-4xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }} data-testid="payment-success-title">
                        You're in. Thank you.
                    </h1>
                    <p className="text-lg text-[#515B57] mb-8">
                        Your subscription is active. Every lesson, every game, every conversation with the tutor — unlocked.
                    </p>
                    <button
                        onClick={() => navigate("/app")}
                        data-testid="payment-success-continue"
                        className="px-8 py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237]"
                    >
                        Back to reading
                    </button>
                </>
            )}
            {status === "timeout" && (
                <>
                    <h1 className="text-3xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }}>Almost there…</h1>
                    <p className="text-[#515B57] mb-6">Stripe is taking longer than usual. Refresh in a moment — your payment is almost certainly through.</p>
                    <Link to="/app" className="text-[#2D5D4E] underline">Go to dashboard</Link>
                </>
            )}
            {(status === "missing" || status === "error") && (
                <>
                    <h1 className="text-3xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }}>Something's off</h1>
                    <p className="text-[#515B57] mb-6">We couldn't find your payment. Try again or reach out to instructor@liftupreading.com.</p>
                    <Link to="/pricing" className="text-[#2D5D4E] underline">Back to pricing</Link>
                </>
            )}
        </main>
    );
}
